import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface CategoryPayload {
    name: string;
    short_description: string;
    description: string;
    code: string | null;
    slug: string | null;
    status: 'active' | 'inactive';
    properties: any[];
}

interface Category {
    id: string;
    name: string;
    short_description: string;
    description: string;
    code: string | null;
    slug: string | null;
    status: 'active' | 'inactive';
    properties: any[];
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
}

@Component({
    selector: 'app-category',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './category.component.html',
    styleUrl: './category.component.scss'
})
export class CategoryComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;

    Category: Category[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 100,
        total: 0,
        last_page: 1
    };

    categoryForm: CategoryPayload = {
        name: '',
        short_description: '',
        description: '',
        code: null,
        slug: null,
        status: 'inactive',
        properties: [
            { code_category: '' }
        ]
    };

    selectedCategoryId: string = '';

    constructor(
        private apiService: ApiService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.getCategories();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Categories
    async getCategories(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/category/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log(response);
            this.Category = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            // console.error('Error fetching categories:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memuat data category',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - Single Category (untuk edit)
    async getCategoryById(categoryId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/category/${categoryId}`
            );

            // Populate form dengan data category
            this.categoryForm = {
                name: response.data.name,
                short_description: response.data.short_description,
                description: response.data.description,
                code: response.data.code,
                slug: response.data.slug,
                status: response.data.status,
                properties: response.data.properties || [{ code_category: '' }]
            };

            this.selectedCategoryId = categoryId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            // console.error('Error fetching category:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Gagal memuat data category',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // POST - Create Category
    async createCategory(): Promise<void> {
        if (!this.validateCategoryForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/category', this.categoryForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Category berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getCategories();
        } catch (error: any) {
            // console.error('Error creating category:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan category',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT - Update Category
    async updateCategory(): Promise<void> {
        if (!this.validateCategoryForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/tenant/manage/category/${this.selectedCategoryId}`,
                this.categoryForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Category berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getCategories(); // Refresh list
        } catch (error: any) {
            // console.error('Error updating category:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate category',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete Category
    async deleteCategory(categoryId: string, categoryName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus category "${categoryName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/tenant/manage/category', categoryId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Category berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getCategories(); // Refresh list
            } catch (error: any) {
                console.error('Error deleting category:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus category',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(categoryId: string): void {
        this.getCategoryById(categoryId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.categoryForm = {
            name: '',
            short_description: '',
            description: '',
            code: null,
            slug: null,
            status: 'inactive',
            properties: [
                { code_category: '' }
            ]
        };
        this.selectedCategoryId = '';
    }

    // ==================== FORM VALIDATION ====================

    validateCategoryForm(): boolean {
        if (!this.categoryForm.name || !this.categoryForm.description) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Name dan Description wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== PAGINATION ====================

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.last_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getCategories();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateCategory();
        } else {
            await this.createCategory();
        }
    }
}
